import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

export const SUPPORT_POLL_MS = 6000;

const EVENT_TITLE: Record<string, string> = {
  invoice_issued: 'Invoice issued',
  invoice_paid: 'Invoice paid',
  limit_changed: 'Server limit changed',
  invoicing_approved: 'Invoice billing approved',
  advisor_reported: 'Advisor reported',
};

export type SupportMessage = {
  id: string;
  side: 'org' | 'platform';
  kind: 'text' | 'event';
  event: string;
  sender_name: string;
  sender_email: string;
  body: string;
  created_at: string;
};

export type SupportConversationRow = {
  id: string;
  organization_id: string;
  organization_name: string;
  organization_kind: 'customer' | 'advisor';
  opened_by_name: string;
  opened_by_email: string;
  assigned_to_email: string;
  mine: boolean;
  last_message_at: string | null;
  last_message_preview: string;
  last_message_side: string;
  unread: number;
  created_at: string;
};

type Thread = { conversation: SupportConversationRow; messages: SupportMessage[] };

export function when(iso: string | null) {
  return iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
}

export default function SupportThread({ conversationId }: { conversationId: string }) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = React.useState('');
  const endRef = React.useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['support-thread', conversationId],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get(`/global-admin/support/conversations/${conversationId}/messages/`);
      return res.data as Thread;
    },
    refetchInterval: SUPPORT_POLL_MS,
    retry: false,
  });

  const messages = thread.data?.messages ?? [];
  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['support-thread', conversationId] });
    queryClient.invalidateQueries({ queryKey: ['support-board'] });
    queryClient.invalidateQueries({ queryKey: ['support-counts'] });
  };

  const send = useMutation({
    mutationFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/support/conversations/${conversationId}/messages/`, { body: draft.trim() });
    },
    onSuccess: () => { setDraft(''); refresh(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Could not send that.'),
  });

  const conversation = thread.data?.conversation;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="support-thread">
      {conversation && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{conversation.organization_name || conversation.organization_id}</p>
            <p className="truncate text-xs text-muted-foreground">
              {conversation.organization_kind === 'advisor' ? 'Advisory practice' : 'Customer'} · opened by {conversation.opened_by_name || conversation.opened_by_email}
              {conversation.assigned_to_email ? ` · handled by ${conversation.assigned_to_email}` : ' · nobody has answered yet'}
            </p>
          </div>
        </div>
      )}
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="support-messages">
        {thread.isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          messages.map((message) => {
            if (message.kind === 'event') {
              return (
                <div key={message.id} className="flex justify-center" data-testid="support-event" data-event={message.event}>
                  <div className="w-full max-w-sm rounded-xl border bg-muted/40 px-4 py-3 text-sm">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      <Bell className="h-3.5 w-3.5" />
                      {EVENT_TITLE[message.event] ?? 'Update'}
                    </div>
                    <p className="mt-1 leading-snug">{message.body}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">{when(message.created_at)}</p>
                  </div>
                </div>
              );
            }
            const ours = message.side === 'platform';
            return (
              <div key={message.id} className={`flex ${ours ? 'justify-end' : 'justify-start'}`} data-testid="support-message" data-side={message.side}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${ours ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  <p className="whitespace-pre-wrap break-words">{message.body}</p>
                  <p className={`mt-1 text-[10px] ${ours ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {message.sender_name || message.sender_email} · {when(message.created_at)}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t p-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Reply as BitSwan support…"
          data-testid="support-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && draft.trim()) { e.preventDefault(); send.mutate(); }
          }}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">The organisation reads this in the AOC and is emailed.</span>
          <Button size="sm" onClick={() => send.mutate()} disabled={send.isPending || !draft.trim()} data-testid="support-send">
            {send.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
            Send
          </Button>
        </div>
      </div>
    </div>
  );
}
